import { ScrollView, Pressable, Linking, Platform, Alert } from "react-native";
import { useRouter, useFocusEffect } from "expo-router";
import * as WebBrowser from "expo-web-browser";
import Constants from "expo-constants";
import { View, Text, Avatar } from "@src/components";
import { Icon } from "@src/components/Icon";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { FEATURE_FLAGS, useFeatureFlags } from "@src/services/feature-flags/feature-flags";
import { useAuth, useTenant, getTenantDisplayName } from "@src/features/tenants";
import { useActionSheet } from "@expo/react-native-action-sheet";
import { secureStorage, SecureStorageKeys } from "@src/services/secure-storage";
import { AppStorageKeys, BiometricPreference, useStorage } from "@src/services/app-storage";
import { useState, useCallback } from "react";
import { useRegionConfig } from "@src/hooks/useRegionConfig";
import LogoIcon from "@src/assets/svgs/logo-icon.svg";
import LogoType from "@src/assets/svgs/logo-type.svg";
import { getDeviceTokenApi } from "@src/features/notifications";
import { useDeviceToken } from "@visioni/a3s-notifications/app";

type MenuItem = {
  id: string;
  title: string;
  onPress: () => void;
  subtitle?: string;
  status?: string;
};

type MenuSection = {
  title: string;
  items: MenuItem[];
};

export default function SettingsScreen() {
  const router = useRouter();
  const { theme } = useUnistyles();
  const { contactUrl, termsUrl, privacyUrl, supportPhone } = useRegionConfig();
  const environment = Constants.expoConfig?.extra?.environment as string | undefined;
  const isProduction = environment === "production";
  const { getBooleanFlag } = useFeatureFlags();
  const isDevPlaygroundEnabled = getBooleanFlag(FEATURE_FLAGS.DEV_PLAYGROUND);
  const isUsersAndPermissionsOptionEnabled = getBooleanFlag(FEATURE_FLAGS.USERS_PERMISSIONS_OPTION);
  const isPushNotificationsSettingsEnabled = getBooleanFlag(FEATURE_FLAGS.PUSH_NOTIFICATIONS);
  const { showActionSheetWithOptions } = useActionSheet();
  const deviceTokenApi = getDeviceTokenApi();

  // Tenant switcher
  const { tenants, selectedTenant } = useTenant();
  const hasMultipleTenants = tenants.length > 1;

  // PIN state
  const [isPinEnabled, setIsPinEnabled] = useState(false);

  // Biometric state
  const [biometricPreference, setBiometricPreference] = useStorage<BiometricPreference>(
    AppStorageKeys.BIOMETRIC_PREFERENCE,
  );
  const isBiometricEnabled = biometricPreference?.biometricEnabled === true;

  const { isRegistered } = useDeviceToken({
    deviceTokenApi,
    enabled: isPushNotificationsSettingsEnabled,
  });

  // Load PIN state whenever the screen comes into focus
  const loadSecurityState = useCallback(async () => {
    const pin = await secureStorage.get(SecureStorageKeys.userPin);
    setIsPinEnabled(Boolean(pin));
  }, []);

  useFocusEffect(
    useCallback(() => {
      loadSecurityState();
    }, [loadSecurityState]),
  );

  const handleBiometricOptions = () => {
    // If biometrics disabled, go directly to setup
    if (!isBiometricEnabled) {
      router.push("/auth/biometrics");
      return;
    }

    // If biometrics enabled, show action sheet with disable option
    const options = ["Disable Face ID", "Cancel"];
    const cancelButtonIndex = 1;
    const destructiveButtonIndex = 0;

    showActionSheetWithOptions(
      {
        options,
        cancelButtonIndex,
        destructiveButtonIndex,
        title: "Face ID",
        message: "Manage your Face ID settings",
      },
      async (buttonIndex?: number) => {
        if (buttonIndex === undefined || buttonIndex === cancelButtonIndex) {
          return;
        }

        // Options: Disable Face ID (0), Cancel (1)
        if (buttonIndex === 0) {
          // Disable biometrics
          Alert.alert(
            "Disable Face ID",
            "Are you sure you want to disable Face ID? You'll need to set it up again to re-enable it.",
            [
              {
                text: "Cancel",
                style: "cancel",
              },
              {
                text: "Disable",
                style: "destructive",
                onPress: async () => {
                  setBiometricPreference({ biometricEnabled: false });
                },
              },
            ],
          );
        }
      },
    );
  };

  const handlePinOptions = () => {
    // If PIN is disabled, go directly to setup
    if (!isPinEnabled) {
      router.push("/auth/pin");
      return;
    }

    // If PIN is enabled, show action sheet with options
    const options = ["Change PIN", "Disable PIN", "Cancel"];
    const cancelButtonIndex = 2;
    const destructiveButtonIndex = 1;

    showActionSheetWithOptions(
      {
        options,
        cancelButtonIndex,
        destructiveButtonIndex,
        title: "App PIN",
        message: "Manage your app PIN settings",
      },
      async (buttonIndex?: number) => {
        if (buttonIndex === undefined || buttonIndex === cancelButtonIndex) {
          return;
        }

        // Options: Change PIN (0), Disable PIN (1), Cancel (2)
        if (buttonIndex === 0) {
          // Change PIN - navigate to change PIN flow
          router.push("/auth/change-pin");
        } else if (buttonIndex === 1) {
          // Disable PIN
          Alert.alert(
            "Disable PIN",
            "Are you sure you want to disable your PIN? You'll need to set it up again to re-enable it.",
            [
              {
                text: "Cancel",
                style: "cancel",
              },
              {
                text: "Disable",
                style: "destructive",
                onPress: async () => {
                  await secureStorage.remove(SecureStorageKeys.userPin);
                  await secureStorage.remove(SecureStorageKeys.legacyUserPin);
                  setIsPinEnabled(false);
                },
              },
            ],
          );
        }
      },
    );
  };

  const handleTenantSwitch = () => {
    router.push("/settings/tenant-select");
  };

  const version = Constants.expoConfig?.version || "1.0.0";
  const buildNumber = Platform.select({
    ios: Constants.expoConfig?.ios?.buildNumber,
    android: Constants.expoConfig?.android?.versionCode?.toString(),
    default: undefined,
  });
  const currentYear = new Date().getFullYear();

  const handleOpenURL = async (url: string) => {
    await WebBrowser.openBrowserAsync(url, {
      presentationStyle: WebBrowser.WebBrowserPresentationStyle.FORM_SHEET,
    });
  };

  const handlePhoneCall = () => {
    const phoneUrl = `tel:${supportPhone}`;
    void Linking.openURL(phoneUrl);
  };

  const handleUsersAndPermissions = () => {
    router.push("/settings/users-and-permissions");
  };

  // Build menu sections
  const generalItems: MenuItem[] = [];

  // Tenant switcher - only show if user has multiple tenants (value on right for consistency with Face ID / App PIN)
  if (hasMultipleTenants) {
    generalItems.push({
      id: "tenant",
      title: "Organisation",
      onPress: handleTenantSwitch,
      status: selectedTenant ? getTenantDisplayName(selectedTenant) : "Not selected",
    });
  }

  // Face ID - only on native platforms
  generalItems.push({
    id: "faceid",
    title: "Face ID",
    onPress: handleBiometricOptions,
    status: isBiometricEnabled ? "Enabled" : "Disabled",
  });

  // App PIN - only on native platforms
  generalItems.push({
    id: "apppin",
    title: "App PIN",
    onPress: handlePinOptions,
    status: isPinEnabled ? "Enabled" : "Disabled",
  });

  if (isUsersAndPermissionsOptionEnabled) {
    generalItems.push({
      id: "users-and-permissions",
      title: "Users & Permissions",
      onPress: handleUsersAndPermissions,
    });
  }

  if (isPushNotificationsSettingsEnabled) {
    generalItems.push({
      id: "app-notifications",
      title: "App Notifications",
      onPress: () => router.push("/settings/app-notifications"),
      status: isRegistered.data?.registered ? "Enabled" : "Disabled",
    });
  }

  const contactItems: MenuItem[] = [
    {
      id: "support",
      title: "Support",
      onPress: handlePhoneCall,
    },
    {
      id: "quote",
      title: "Get a Quote",
      onPress: () => handleOpenURL(contactUrl),
    },
    {
      id: "contact",
      title: "Contact Us",
      onPress: () => handleOpenURL(contactUrl),
    },
  ];

  const legalItems: MenuItem[] = [
    {
      id: "terms",
      title: "Terms & Conditions",
      onPress: () => handleOpenURL(termsUrl),
    },
    {
      id: "privacy",
      title: "Privacy",
      onPress: () => handleOpenURL(privacyUrl),
    },
  ];

  const developmentItems: MenuItem[] = [
    {
      id: "playground",
      title: "Playground",
      onPress: () => router.push("/development"),
    },
  ];

  // Build sections array
  const sections: MenuSection[] = [
    { title: "General", items: generalItems },
    { title: "Contact", items: contactItems },
    { title: "Legal", items: legalItems },
  ];

  // Only add Development section in non-production environments
  if (!isProduction) {
    sections.push({ title: "Development", items: developmentItems });
  }

  const renderMenuItem = (item: MenuItem, isLast: boolean) => (
    <Pressable
      key={item.id}
      style={{
        flexDirection: "row",
        alignItems: "center",
        paddingVertical: theme.spacing["3xl"],
        paddingHorizontal: theme.spacing.xl,
        borderBottomWidth: isLast ? 0 : 1,
        borderBottomColor: theme.colors.border,
      }}
      onPress={item.onPress}
    >
      <View flex={1}>
        <Text textStyle="md/medium">{item.title}</Text>
      </View>
      {item.status != null && (
        <Text textStyle="xs/regular" color="textMuted" numberOfLines={1} style={{ marginRight: theme.spacing.lg }}>
          {item.status}
        </Text>
      )}
      <Icon name="chevronRight" size={theme.spacing["7xl"]} color={theme.colors.textMuted} />
    </Pressable>
  );

  const renderSection = (section: MenuSection) => {
    if (section.title === "Development" && !isDevPlaygroundEnabled) {
      return null;
    }
    return (
      <View key={section.title} mb="4xl">
        <Text textStyle="xs/medium" mb="md" style={styles.sectionTitle}>
          {section.title}
        </Text>
        <View bg="surfaceCard" style={styles.sectionCard}>
          {section.items.map((item, index) => renderMenuItem(item, index === section.items.length - 1))}
        </View>
      </View>
    );
  };

  // Get user from auth context
  const { user } = useAuth();

  // Display name from user.name, or "Logout" if no name
  const userName = user?.name || "Logout";

  const handleLogout = () => {
    router.push("/auth/logout");
  };

  return (
    <View flex={1} bg="background">
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.contentContainerStyle}>
        {/* User Profile Row */}
        <Pressable onPress={handleLogout}>
          <View bg="surfaceCard" style={styles.userProfileRow}>
            {/* Avatar */}
            <View mr="3xl">
              <Avatar name={user?.name} icon={user?.name ? undefined : "userOutline"} size="sm" />
            </View>

            {/* User Name */}
            <Text textStyle="md/medium" style={styles.usernameText}>
              {userName}
            </Text>

            {/* Logout Icon */}
            <Icon name="logout" size={theme.spacing["6xl"]} color={theme.colors.textMuted} />
          </View>
        </Pressable>

        {/* Menu Sections */}
        {sections.map(renderSection)}

        {/* Footer Card */}
        <View bg="surface" style={styles.footerCard}>
          {/* Vision Intelligence Logo with Text */}
          <View flexDirection="row" alignItems="center" mb="3xl">
            <LogoIcon width={50} height={45} />
            <LogoType width={130} height={45} style={styles.logoType} />
          </View>

          {/* App Version */}
          <Text textStyle="xs/regular" color="textMuted">
            App Version {version}
            {buildNumber ? ` (${buildNumber})` : ""}
          </Text>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  scrollView: {
    flex: 1,
  },
  contentContainerStyle: {
    padding: theme.spacing["3xl"],
    paddingBottom: 40,
  },
  logoType: {
    color: theme.colors.text,
  },
  userProfileRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: theme.spacing["3xl"],
    paddingHorizontal: theme.spacing.xl,
    borderRadius: theme.spacing.xl,
    borderWidth: 1,
    borderColor: theme.colors.border,
    marginBottom: theme.spacing["4xl"],
  },
  usernameText: {
    flex: 1,
  },
  sectionTitle: {
    color: theme.colors.interactive.controls.default_midground,
  },
  sectionCard: {
    borderRadius: theme.spacing.xl,
    borderWidth: 1,
    borderColor: theme.colors.border,
    overflow: "hidden",
  },
  footerCard: {
    borderRadius: theme.spacing.xl,
    borderWidth: 1,
    borderColor: theme.colors.border,
    padding: theme.spacing["4xl"],
    alignItems: "flex-start",
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: theme.spacing["3xl"],
    paddingHorizontal: theme.spacing.xl,
    borderBottomColor: theme.colors.border,
  },
  menuItemText: {
    flex: 1,
  },
  menuItemSubtitle: {
    marginRight: theme.spacing.lg,
  },
}));
